import { useState, useEffect, useRef, useCallback } from 'react';
import { unifiedApiService } from '../../services/unified';
import type { EnhancedConversationMessage } from '../../services/unified/types';

// Authenticated Image Component - fetches image with auth token and displays as blob
function AuthenticatedImage({ 
  filename, 
  alt, 
  className, 
  onClick 
}: { 
  filename: string; 
  alt: string; 
  className?: string; 
  onClick?: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);
        const blob = await unifiedApiService.messaging.getAttachment(filename);
        if (mounted) {
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
        }
      } catch (err) {
        console.error('Failed to load image:', err);
        if (mounted) setError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadImage();
    return () => {
      mounted = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [filename]);

  if (loading) {
    return (
      <div className={`${className} bg-gray-200 animate-pulse flex items-center justify-center`}>
        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center text-gray-400`}>
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    );
  }

  return <img src={blobUrl} alt={alt} className={className} onClick={onClick} />;
}

interface CandidateMessagingProps {
  candidateId: string;
  candidateName: string;
}

export function CandidateMessaging({ candidateId, candidateName }: CandidateMessagingProps) {
  const [messages, setMessages] = useState<EnhancedConversationMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<{ url: string; filename: string; isBlob: boolean } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Handle preview with authenticated fetch
  const handlePreviewAttachment = useCallback(async (filename: string, originalFilename: string) => {
    try {
      const blob = await unifiedApiService.messaging.getAttachment(filename);
      const url = URL.createObjectURL(blob);
      setPreviewFile({ url, filename: originalFilename, isBlob: true });
    } catch (err) {
      console.error('Failed to load preview:', err);
      setError('Failed to load preview');
    }
  }, []);

  // Close preview and cleanup blob URL
  const closePreview = useCallback(() => {
    if (previewFile?.isBlob) {
      URL.revokeObjectURL(previewFile.url);
    }
    setPreviewFile(null);
  }, [previewFile]);

  const isImageFile = (filename: string) => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '');
  };

  const isPdfFile = (filename: string) => {
    return filename.toLowerCase().endsWith('.pdf');
  };

  // Load messages on mount and poll every 5 seconds
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        // Use admin endpoint to get messages for this candidate
        const messagesData = await unifiedApiService.messaging.getAdminConversations(candidateId);
        setMessages(messagesData as EnhancedConversationMessage[]);
        setError(null);
        
        // Scroll to bottom
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } catch (err) {
        console.error('Failed to fetch messages:', err);
        setError('Failed to load messages');
      }
    };

    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    
    return () => clearInterval(interval);
  }, [candidateId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    // Validate file sizes (max 10MB per file)
    const maxSize = 10 * 1024 * 1024;
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        setError(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });
    
    setAttachments(prev => [...prev, ...validFiles]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && attachments.length === 0)) return;

    setSendingMessage(true);
    setError(null);
    
    try {
      // Use admin endpoint to send message to this candidate
      await unifiedApiService.messaging.sendAdminMessage(candidateId, {
        message: newMessage,
      });
      
      // Clear form
      setNewMessage('');
      setAttachments([]);
      setUploadProgress(0);
      
      // Refresh messages immediately using admin endpoint
      const messagesData = await unifiedApiService.messaging.getAdminConversations(candidateId);
      setMessages(messagesData as EnhancedConversationMessage[]);
      
      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message');
    } finally {
      setSendingMessage(false);
      setUploadProgress(0);
    }
  };

  const handleDownloadAttachment = async (filename: string, originalFilename: string) => {
    try {
      const blob = await unifiedApiService.messaging.getAttachment(filename);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download attachment:', err);
      setError('Failed to download attachment');
    }
  };

  return (
    <div className="flex flex-col" style={{ height: '600px', maxHeight: '70vh' }}>
      <h3 className="text-lg font-medium text-gray-900 mb-4 px-6 pt-6 flex-shrink-0">
        Messages with {candidateName}
      </h3>
      
      {error && (
        <div className="mx-6 mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex-shrink-0">
          {error}
        </div>
      )}

      {/* Messages - Fixed height with scroll */}
      <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No messages yet</h3>
            <p className="mt-1 text-sm text-gray-500">Start a conversation with this candidate below.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => {
              const isAdmin = message.sender.toLowerCase().includes('admin');
              return (
                <div
                  key={message.id}
                  className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      isAdmin
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <p className={`text-xs font-medium ${
                        isAdmin ? 'text-primary-100' : 'text-gray-600'
                      }`}>
                        {message.sender}
                      </p>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{message.message}</p>
                    
                    {/* Attachments - WhatsApp Style */}
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {message.attachments.map((attachment, idx) => {
                          const isImage = isImageFile(attachment.original_filename);
                          const isPdf = isPdfFile(attachment.original_filename);
                          
                          if (isImage) {
                            return (
                              <div key={idx} className="relative group rounded-lg overflow-hidden">
                                <AuthenticatedImage
                                  filename={attachment.filename}
                                  alt={attachment.original_filename}
                                  className="max-w-[180px] max-h-[180px] object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => handlePreviewAttachment(attachment.filename, attachment.original_filename)}
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
                                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 pointer-events-auto">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handlePreviewAttachment(attachment.filename, attachment.original_filename); }}
                                      className="p-1.5 bg-white/90 text-gray-700 rounded-full shadow-lg hover:bg-white"
                                      title="Preview"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleDownloadAttachment(attachment.filename, attachment.original_filename); }}
                                      className="p-1.5 bg-white/90 text-gray-700 rounded-full shadow-lg hover:bg-white"
                                      title="Download"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          
                          // PDF and other files - card style
                          return (
                            <div
                              key={idx}
                              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                                isAdmin 
                                  ? 'bg-primary-500/80 hover:bg-primary-400/80' 
                                  : 'bg-gray-200 hover:bg-gray-300'
                              }`}
                              onClick={() => isPdf ? handlePreviewAttachment(attachment.filename, attachment.original_filename) : handleDownloadAttachment(attachment.filename, attachment.original_filename)}
                            >
                              <div className={`p-1.5 rounded-lg ${isAdmin ? 'bg-primary-400/50' : 'bg-white'}`}>
                                {isPdf ? (
                                  <svg className={`w-4 h-4 ${isAdmin ? 'text-white' : 'text-red-500'}`} fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4z"/>
                                  </svg>
                                ) : (
                                  <svg className={`w-4 h-4 ${isAdmin ? 'text-white' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-xs font-medium truncate ${isAdmin ? 'text-white' : 'text-gray-900'}`}>
                                  {attachment.original_filename}
                                </p>
                                <p className={`text-[10px] ${isAdmin ? 'text-primary-200' : 'text-gray-500'}`}>
                                  {(attachment.file_size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                              <svg className={`w-4 h-4 flex-shrink-0 ${isAdmin ? 'text-primary-200' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    <p className={`text-xs mt-1 ${
                      isAdmin ? 'text-primary-100' : 'text-gray-500'
                    }`}>
                      {message.created_at ? new Date(message.created_at).toLocaleString() : 'Just now'}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message Input - Fixed at bottom */}
      <div className="border-t border-gray-200 p-6 flex-shrink-0 bg-white">
        <form onSubmit={handleSendMessage} className="space-y-3">
          {/* Attachments Preview */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {attachments.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 bg-gray-100 px-3 py-1 rounded-md text-sm"
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  <span className="text-gray-700 truncate max-w-xs">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(index)}
                    className="text-gray-500 hover:text-red-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Upload Progress */}
          {sendingMessage && uploadProgress > 0 && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}
          
          <div className="flex space-x-3">
            <div className="flex-1">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex flex-col space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md text-sm font-medium"
                title="Attach files"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </button>
              <button
                type="submit"
                disabled={sendingMessage || (!newMessage.trim() && attachments.length === 0)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
              >
                {sendingMessage ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* File Preview Modal */}
      {previewFile && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={closePreview}
        >
          <div className="relative max-w-4xl w-full max-h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-900 truncate">
                {previewFile.filename}
              </h3>
              <div className="flex items-center gap-2">
                <a
                  href={previewFile.url}
                  download={previewFile.filename}
                  className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Download"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </a>
                <button
                  onClick={closePreview}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-4 overflow-auto max-h-[calc(90vh-60px)] bg-gray-100 flex items-center justify-center">
              {previewFile.filename.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={previewFile.url}
                  className="w-full h-[70vh] rounded-lg border border-gray-200 bg-white"
                  title="PDF Preview"
                />
              ) : (
                <img
                  src={previewFile.url}
                  alt={previewFile.filename}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
